import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import type {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { useCarouselAnalytics } from "src/website-apps/shared/components/carousel-module/use-carousel-analytics";

export interface UsePerformerCarouselPageEventsOptions {
  scrollContainerRef: RefObject<HTMLDivElement>;
  itemCount: number;
  isAtEnd: boolean;
  carouselId: CarouselId;
  itemType?: CarouselItemType | undefined;
}

/**
 * Page event tracking for the performer carousel module.
 * (currently used for Recently Viewed Events experiment but designed to be reusable for other carousels on the performer page)
 *
 * Tracks:
 * - Arrow clicks (left/right) for desktop/tablet navigation
 * - Swipe gestures (left/right) for mobile/tablet touch navigation
 * - Scroll end (when user reaches the last item)
 * - Item clicks via injected fireItemClick callback
 */
export const usePerformerCarouselPageEvents = ({
  scrollContainerRef,
  itemCount,
  isAtEnd,
  carouselId,
  itemType,
}: UsePerformerCarouselPageEventsOptions) => {
  // TODO [CWP-2144]: Legacy PE events below are superseded by CarouselInteraction UAE.
  // Remove once analytics team migrates queries off the performer carousel PE keys.
  const storePageEvent = useStorePageEvent();
  const prevIsAtEnd = useRef(false);
  const { categoryId } = usePerformerPageModelData().categorySummary;

  const fireSwipe = useCallback(
    (direction: "left" | "right") => {
      storePageEvent(
        PE_TYPE.LOGGING,
        PE_KEYS.PERFORMER_CAROUSEL_MODULE_SWIPE,
        JSON.stringify({ carouselId, direction, itemCount }),
      );
    },
    [carouselId, itemCount, storePageEvent],
  );

  const {
    carouselInViewRef,
    fireArrowClickEvent: fireArrowUAE,
    fireItemClick: fireItemClickUAE,
    fireItemHover,
  } = useCarouselAnalytics({
    scrollContainerRef,
    itemCount,
    carouselId,
    itemType,
    categoryId,
    onSwipe: fireSwipe,
  });

  const fireArrowClickEvent = useCallback(
    (direction: "left" | "right") => {
      storePageEvent(
        PE_TYPE.LOGGING,
        PE_KEYS.PERFORMER_CAROUSEL_MODULE_ARROW_CLICK,
        JSON.stringify({ carouselId, direction, itemCount }),
      );
      fireArrowUAE(direction);
    },
    [carouselId, itemCount, fireArrowUAE, storePageEvent],
  );

  const fireItemClick = useCallback(
    (itemId: string | number, position: number) => {
      storePageEvent(
        PE_TYPE.LOGGING,
        PE_KEYS.PERFORMER_CAROUSEL_MODULE_ITEM_CLICK,
        JSON.stringify({ carouselId, value: itemId, position, itemCount }),
      );
      fireItemClickUAE(itemId, position);
    },
    [carouselId, itemCount, fireItemClickUAE, storePageEvent],
  );

  useEffect(() => {
    if (isAtEnd && !prevIsAtEnd.current) {
      storePageEvent(
        PE_TYPE.LOGGING,
        PE_KEYS.PERFORMER_CAROUSEL_MODULE_SCROLL_END,
        JSON.stringify({ carouselId, itemCount }),
      );
    }
    prevIsAtEnd.current = isAtEnd;
  }, [isAtEnd, carouselId, itemCount, storePageEvent]);

  return { carouselInViewRef, fireArrowClickEvent, fireItemClick, fireItemHover, categoryId };
};
